import { NgTemplateOutlet } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MatCardModule } from '@angular/material/card'
import { MatIconModule } from '@angular/material/icon'
import { IconLoadingDirective } from '../../directives/icon-loading.directive'
import { InfoCardComponent } from '../cards/info-card.component'
import { ConnectionService } from '../../services/connection.service'
import { LIB_ANREDE } from '../../provider'
import { HttpFailureKind, httpFailureCopy, isRecoverableFailure } from '../../util/http-failure.util'

/**
 * Der Fehlerzustand einer Ansicht: Icon, Ueberschrift, ein Satz zur Ursache und der Weg heraus.
 *
 * Er ersetzt die frueheren Einzeiler („Fehler beim Laden der Daten. Probiere, die Seite neu zu
 * laden") an allen Stellen, an denen `lib-resource-loading` steht. Zwei Dinge machen ihn aus:
 * er benennt die **Ursache** (kein Netz / SaniUp startet / Sitzung abgelaufen / keine
 * Berechtigung), und er bietet den Weg heraus als Knopf an, statt einen Browser-Reload zu
 * verlangen — der wirft in einem halb ausgefuellten Assistenten die Arbeit weg.
 *
 * Laeuft die Selbstheilung (`ConnectionService`), zeigt er zusaetzlich den Countdown bis zum
 * naechsten Versuch. Ohne den sieht ein automatischer Neuversuch wie ein zufaelliges Flackern aus.
 */
@Component({
  selector: 'lib-error-state',
  imports: [NgTemplateOutlet, MatButtonModule, MatCardModule, MatIconModule, IconLoadingDirective, InfoCardComponent],
  templateUrl: './error-state.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './error-state.component.scss',
  host: {
    '[class.variant-page]': "variant() === 'page'",
    '[class.tone-warn]': "tone() === 'warn'",
    '[class.tone-error]': "tone() === 'error'",
  },
})
export class ErrorStateComponent {
  private readonly connectionService = inject(ConnectionService)
  private readonly anrede = inject(LIB_ANREDE)

  readonly kind = input<HttpFailureKind>('unknown')

  /**
   * `inline` steht in einer Seite oder Karte, `page` traegt eine ganze Ansicht (groesseres Icon,
   * zentriert, mehr Luft).
   */
  readonly variant = input<'inline' | 'page'>('inline')

  /** Ein Versuch laeuft gerade — der Knopf zeigt es, statt sich stumm nicht zu ruehren. */
  readonly retrying = input(false)

  readonly retry = output<void>()

  readonly copy = computed(() => httpFailureCopy(this.kind(), this.anrede))

  /**
   * Wie laut die Flaeche sein darf. Nur ein echter Fehlschlag bekommt den Fehlerton — „SaniUp
   * startet gerade" ist eine Warnung, „Sitzung abgelaufen" ein Sachstand.
   */
  readonly tone = computed<'warn' | 'error' | 'info'>(() => {
    const kind = this.kind()
    if (isRecoverableFailure(kind)) {
      return 'warn'
    }
    return kind === 'server' || kind === 'unknown' ? 'error' : 'info'
  })

  /**
   * Der Countdown gehoert nur an einen Fehler, den die Selbstheilung auch anfasst. An einem 500er
   * oder einer fehlenden Berechtigung waere er eine Zusage, die niemand einloest.
   */
  readonly secondsUntilRetry = computed(() => (isRecoverableFailure(this.kind()) ? this.connectionService.secondsUntilRetry() : null))
}
